import styled from 'styled-components';
import {
  CLINIC_MARKETPLACE_PAGE,
  COACH_MARKETPLACE_PAGE,
  COURT_FINDER_PAGE,
  EXPLORE_ALL_MARKETPLACES,
  LEAGUE_MARKETPLACE_PAGE,
  OPEN_PLAY_MARKETPLACE_PAGE,
  SOCIAL_MARKETPLACE_PAGE,
  TOURNAMENTS_PAGE,
} from 'constants/pages';
import { type MarketplaceLinkParams, marketplaceUrl } from 'utils/shared/url/marketplaceUrl';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

const imgCompetitions = '/images/player-home-v2/discover/competitions.png';
const imgLessons = '/images/player-home-v2/discover/lessons.png';
const imgClinics = '/images/player-home-v2/discover/clinics.png';
const imgSocials = '/images/player-home-v2/discover/socials.png';
const imgOpenPlays = '/images/player-home-v2/discover/open-plays.png';
const imgCourts = '/images/player-home-v2/discover/courts.png';
const imgLeagues = '/images/player-home-v2/discover/leagues.png';
const imgExploreAllGlow = '/images/player-home-v2/discover/explore-all-glow.svg';
const imgExploreAllMagnifier = '/images/player-home-v2/discover/explore-all-magnifier.svg';

interface DiscoverCard {
  title: string;
  href: string;
  image: string;
}

const cards: DiscoverCard[] = [
  { title: 'Tournaments', href: TOURNAMENTS_PAGE, image: imgCompetitions },
  { title: 'Leagues', href: LEAGUE_MARKETPLACE_PAGE, image: imgLeagues },
  { title: 'Lessons', href: COACH_MARKETPLACE_PAGE, image: imgLessons },
  { title: 'Clinics', href: CLINIC_MARKETPLACE_PAGE, image: imgClinics },
  { title: 'Socials', href: SOCIAL_MARKETPLACE_PAGE, image: imgSocials },
  { title: 'Open plays', href: OPEN_PLAY_MARKETPLACE_PAGE, image: imgOpenPlays },
  { title: 'Courts', href: COURT_FINDER_PAGE, image: imgCourts },
];

const CARD_LINK_CLASS = 'min-w-[10.5rem] flex-1 max-sm:min-w-[8rem] xl:min-w-0';
const CARD_BODY_CLASS =
  'group relative flex h-[8.75rem] w-full rounded-3xl px-0 py-ds-lg max-sm:h-[6.5rem]';
const CARD_LABEL_CLASS =
  'typography-product-heading-compact-desktop max-sm:typography-product-heading-compact-mobile text-center text-color-text-lightmode-invert dark:text-color-text-darkmode-invert';

const CardImage = styled.div<{ image: string }>`
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.05) 34.45%, #000 100%),
    url(${({ image }) => image}) lightgray 50% / cover no-repeat;
`;

export default function DiscoverSection({
  title,
  className,
  marketplaceParams,
}: {
  title?: string;
  className?: string;
  /** Set from Home, so a card lands on the marketplace with that sport and area. */
  marketplaceParams?: MarketplaceLinkParams;
}) {
  return (
    <section
      className={classNames(
        'container mx-auto mt-ds-3xl max-sm:mt-[3.75rem] max-sm:px-4',
        className,
      )}
    >
      {title && (
        <h3 className="max-lg:typography-informative-subheading-medium-highlight lg:typography-product-heading-desktop mb-4">
          {title}
        </h3>
      )}
      <div className="grid grid-cols-2 gap-ds-lg pb-1 max-sm:gap-ds-md md:grid-cols-4 xl:grid-cols-8">
        {cards.map((card) => (
          <Link
            key={card.title}
            href={marketplaceUrl(card.href, marketplaceParams)}
            className={CARD_LINK_CLASS}
          >
            <CardImage
              image={card.image}
              className={classNames(CARD_BODY_CLASS, 'items-end justify-center')}
            >
              <span
                className={classNames(
                  CARD_LABEL_CLASS,
                  'transition-transform duration-200 group-hover:-translate-y-[20px]',
                )}
              >
                {card.title}
              </span>
              <div className="absolute bottom-0 left-1/2 flex h-14 w-14 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full bg-color-bg-lightmode-primary opacity-0 shadow-[0_4px_20px_0_rgba(0,0,0,0.16)] transition-opacity duration-300 group-hover:opacity-100 dark:bg-color-bg-darkmode-primary">
                <img
                  src="/images/player-home-v2/discover/search-colored.png"
                  alt="search-icon"
                  className="h-5 w-5"
                />
              </div>
            </CardImage>
          </Link>
        ))}
        <Link href={EXPLORE_ALL_MARKETPLACES} className={CARD_LINK_CLASS}>
          <div
            className={classNames(
              CARD_BODY_CLASS,
              'flex-col items-center justify-end gap-ds-sm overflow-hidden bg-color-bg-lightmode-invert dark:border dark:border-color-border-input-darkmode',
            )}
          >
            <div className="relative flex h-8 w-8 items-center justify-center transition-transform duration-200 group-hover:scale-110">
              <img
                src={imgExploreAllGlow}
                alt=""
                className="pointer-events-none absolute left-1/2 top-1/2 h-[9.4375rem] w-[9.8125rem] max-w-none -translate-x-1/2 -translate-y-1/2"
              />
              <img src={imgExploreAllMagnifier} alt="" className="relative h-8 w-8" />
            </div>
            <span className={CARD_LABEL_CLASS}>Explore all</span>
          </div>
        </Link>
      </div>
    </section>
  );
}
